import React, {useCallback, useEffect, useMemo, useState} from "react";
import {useTranslation} from "react-i18next";
import {Alert, Box, CircularProgress, DialogActions, DialogContent, DialogTitle, Typography} from "@mui/material";
import Grid2 from "@mui/material/Unstable_Grid2";
import {ArrowForward} from "@mui/icons-material";
import Dialog from "../../base/dialog";
import Button from "../../base/button/button";
import IconButton from "../../base/button/icon_button";
import Form, {MuiRFF, Yup} from "../../form";
import ValidateMessages from "../../../../core/models/static/texts/vallidate-messages";
import Api from "../../../../core/services/api/api";
import useIsMounted from "../../../hooks/use-is-mounted";
import CompetitorAutocomplete from "../../text-fields/autocomplete/competitor-autocomplete";
import PartAutocomplete from "../../text-fields/autocomplete/part-autocomplete";
import CustomerAutocomplete from "../../text-fields/autocomplete/customer-autocomplete";
import ManageCompetitorsDialog from "../manage-competitors";
import {formatMoney} from "../../../../core/services/utils/utils";
import useCachedPriceLevelsDictionaryController from "../../../hooks/use-price-level-dictionary";

/**
 * Reports a competitor price. Part-info mode receives part + customer; page mode lets the operator pick both.
 */
const ReportCompetitorPriceDialog = ({
                                         open,
                                         onClose,
                                         setOpen,
                                         part,
                                         customer,
                                         onSuccess,
                                     }) => {
    const {t} = useTranslation('dialogs', {keyPrefix: 'app-specific.report-competitor-price'});
    const isMounted = useIsMounted();
    const isPageMode = !part?.id;
    const [submitting, setSubmitting] = useState(false);
    const [manageOpen, setManageOpen] = useState(false);
    const [competitorsVersion, setCompetitorsVersion] = useState(0);
    const [selectedPart, setSelectedPart] = useState(part ?? null);
    const [selectedCustomer, setSelectedCustomer] = useState(customer ?? null);
    const [currentPrice, setCurrentPrice] = useState(null);
    const [priceLevelId, setPriceLevelId] = useState(null);
    const [priceLevelTitle, setPriceLevelTitle] = useState('');
    const [priceLoading, setPriceLoading] = useState(false);
    const {priceLevelDict, getPriceLevelDictionary} = useCachedPriceLevelsDictionaryController({loadDict: true});

    const closeDialog = useCallback(() => {
        onClose?.();
        setOpen?.(false);
    }, [onClose, setOpen]);

    const schema = useMemo(() => Yup.object({
        competitor: Yup.mixed().nullable().required(ValidateMessages.required),
        competitorPrice: Yup.number().nullable().required(ValidateMessages.required).min(0, ValidateMessages.greaterThan(formatMoney(0))),
        ...(isPageMode ? {
            part: Yup.mixed().nullable().required(ValidateMessages.required),
            customer: Yup.mixed().nullable().required(ValidateMessages.required),
        } : {}),
    }), [isPageMode]);
    const validate = useMemo(() => MuiRFF.makeValidate(schema), [schema]);
    const required = useMemo(() => MuiRFF.makeRequired(schema), [schema]);

    /**
     * Resets local state when the dialog opens.
     */
    useEffect(() => {
        if (!open)
            return;
        setSelectedPart(part ?? null);
        setSelectedCustomer(customer ?? null);
        setCurrentPrice(null);
        setPriceLevelId(null);
        setPriceLevelTitle('');
        void getPriceLevelDictionary();
    }, [customer, getPriceLevelDictionary, open, part]);

    /**
     * Fetches the customer's current price and price level for the selected part.
     */
    const fetchCurrentPrice = useCallback(async (partEntity, customerEntity) => {
        const partNumber = partEntity?.partNo ?? partEntity?.partNumber;
        if (!partNumber || !customerEntity?.id) {
            setCurrentPrice(null);
            setPriceLevelId(null);
            setPriceLevelTitle('');
            return;
        }
        setPriceLoading(true);
        const response = await Api.getPriceForCustomer({
            partNumbers: [partNumber],
            customerId: customerEntity.id,
        });
        if (!isMounted())
            return;
        setPriceLoading(false);
        if (!response?.resultFlag) {
            setCurrentPrice(null);
            setPriceLevelId(null);
            setPriceLevelTitle('');
            return;
        }
        const row = response.data?.find((entry) => entry.partNo === partNumber) ?? response.data?.[0];
        const nextPriceLevelId = row?.priceLevelId ?? null;
        setCurrentPrice(row?.price ?? null);
        setPriceLevelId(nextPriceLevelId);
        setPriceLevelTitle(
            priceLevelDict?.find((entry) => entry.priceLevelId === nextPriceLevelId)?.title
            ?? row?.priceLevelTitle
            ?? '',
        );
    }, [isMounted, priceLevelDict]);

    useEffect(() => {
        if (!open)
            return;
        void fetchCurrentPrice(selectedPart, selectedCustomer);
    }, [fetchCurrentPrice, open, selectedPart, selectedCustomer]);

    /**
     * Ensures the competitor has an id (Add when freeSolo), then posts ReportPrice.
     */
    const submit = useCallback(async (values) => {
        let competitor = values.competitor;
        const title = typeof competitor === 'string' ? competitor.trim() : competitor?.isNew ? competitor.title?.trim() : '';
        if (!competitor?.id && title) {
            const created = await Api.addCompetitor({title});
            if (!isMounted())
                return;
            if (!created?.resultFlag)
                return;
            competitor = created.data;
        }
        const partEntity = isPageMode ? values.part : selectedPart;
        const customerEntity = isPageMode ? values.customer : selectedCustomer;
        if (!competitor?.id || !partEntity?.id || !customerEntity?.id || currentPrice == null || !priceLevelId) {
            return;
        }
        setSubmitting(true);
        const response = await Api.reportCompetitorPrice({
            partId: Number(partEntity.id),
            currentPrice: Number(currentPrice),
            competitorId: Number(competitor.id),
            competitorPrice: Number(values.competitorPrice),
            customerId: Number(customerEntity.id),
            priceLevelId: Number(priceLevelId),
        });
        if (!isMounted())
            return;
        setSubmitting(false);
        if (response?.resultFlag) {
            onSuccess?.();
            closeDialog();
        }
    }, [closeDialog, currentPrice, isMounted, isPageMode, onSuccess, priceLevelId, selectedCustomer, selectedPart]);

    const canSubmit = currentPrice != null && !!priceLevelId && !priceLoading;
    const contextPart = selectedPart?.partNumber ?? selectedPart?.partNo ?? '';
    const contextCustomer = selectedCustomer?.name ?? '';
    const contextPriceLevel = priceLoading
        ? t('loadingPrice')
        : (priceLevelTitle || t('priceLevelUnknown'));

    /**
     * Copies the fetched current price into the competitor-price field.
     */
    const applyCurrentPrice = useCallback((form) => {
        if (currentPrice == null)
            return;
        form.change('competitorPrice', currentPrice);
    }, [currentPrice]);

    return (
        <>
            <Dialog
                open={open}
                setOpen={(nextOpen) => {
                    if (nextOpen === false)
                        closeDialog();
                }}
                maxWidth={'sm'}
                fullWidth
                loading={submitting}
                disableBackdropClick={submitting}
                disableEscapeKeyDown={submitting}
            >
                <DialogTitle fontWeight={600}>
                    {t('title')}
                </DialogTitle>
                <DialogContent dividers>
                    <Form
                        onSubmit={submit}
                        validate={validate}
                        initialValues={{}}
                        formProps={{id: 'report-competitor-price-form'}}
                    >
                        {({form}) => (
                            <Grid2 container spacing={2} sx={{pt: 1}}>
                                {
                                    !!contextCustomer &&
                                    <Grid2 xs={12}>
                                        <Alert severity={'info'}>
                                            {
                                                !!contextPart &&
                                                <>
                                                    {t('contextPart', {part: contextPart})}
                                                    {' '}
                                                </>
                                            }
                                            {t('contextCustomer', {
                                                customer: contextCustomer,
                                                priceLevel: contextPriceLevel,
                                            })}
                                        </Alert>
                                    </Grid2>
                                }
                                {
                                    isPageMode &&
                                    <>
                                        <Grid2 xs={12}>
                                            <PartAutocomplete
                                                form
                                                name={'part'}
                                                required={required.part}
                                                label={t('part')}
                                                onChange={(_, value) => {
                                                    setSelectedPart(value);
                                                    form.change('part', value);
                                                }}
                                            />
                                        </Grid2>
                                        <Grid2 xs={12}>
                                            <CustomerAutocomplete
                                                form
                                                name={'customer'}
                                                required={required.customer}
                                                label={t('customer')}
                                                onChange={(_, value) => {
                                                    setSelectedCustomer(value);
                                                    form.change('customer', value);
                                                }}
                                            />
                                        </Grid2>
                                    </>
                                }
                                <Grid2 xs={12}>
                                    <CompetitorAutocomplete
                                        form
                                        name={'competitor'}
                                        required={required.competitor}
                                        label={t('competitor')}
                                        dependencies={[competitorsVersion]}
                                        onManageClick={() => setManageOpen(true)}
                                    />
                                </Grid2>
                                <Grid2 xs={12}>
                                    <Box sx={{display: 'flex', alignItems: 'center', gap: 0.5, mb: 1}}>
                                        <Typography
                                            variant={'caption'}
                                            fontWeight={500}
                                            color={(!priceLoading && currentPrice == null) ? 'error.main' : 'text.primary'}
                                        >
                                            {t('currentPrice')}:{' '}
                                            {
                                                priceLoading
                                                    ? <CircularProgress size={14}/>
                                                    : currentPrice != null
                                                        ? formatMoney(currentPrice)
                                                        : t('priceUnavailable')
                                            }
                                        </Typography>
                                        {
                                            !priceLoading && currentPrice != null &&
                                            <IconButton
                                                disabled={submitting}
                                                color={'paper.secondary'}
                                                onPressed={() => applyCurrentPrice(form)}
                                                aria-label={t('useCurrentPrice')}
                                            >
                                                <ArrowForward className={'icon normal'}/>
                                            </IconButton>
                                        }
                                    </Box>
                                    <MuiRFF.TextField
                                        name={'competitorPrice'}
                                        label={t('competitorPrice')}
                                        type={'number'}
                                        required={required.competitorPrice}
                                        fullWidth
                                        disabled={submitting}
                                    />
                                </Grid2>
                            </Grid2>
                        )}
                    </Form>
                </DialogContent>
                <DialogActions className={'p-0'}>
                    <Box sx={{
                        margin: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        width: '100%',
                    }}>
                        <Button
                            disabled={submitting}
                            variant={'dialog'}
                            color={'primary'}
                            onClick={closeDialog}
                            borderRadius={'0 8px 0 8px'}
                        >
                            {t('cancel')}
                        </Button>
                        <Button
                            disabled={submitting || !canSubmit}
                            variant={'dialog'}
                            color={'primary'}
                            type={'submit'}
                            form={'report-competitor-price-form'}
                            borderRadius={'8px 0 8px 0'}
                        >
                            {submitting ? t('submitting') : t('submit')}
                        </Button>
                    </Box>
                </DialogActions>
            </Dialog>
            {
                manageOpen &&
                <ManageCompetitorsDialog
                    open={manageOpen}
                    onClose={() => setManageOpen(false)}
                    onChanged={() => setCompetitorsVersion((prev) => prev + 1)}
                />
            }
        </>
    );
};

export default ReportCompetitorPriceDialog;
